import { useEffect, useMemo, useState } from 'react'
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react'
import { createPortal } from 'react-dom'
import { api } from '../lib/api'
import { audioCacheKey } from '../lib/audioDownload'
import { audioStore } from '../lib/blobStore'
import { downloadSegnoFile } from '../lib/segnoFile'
import { ownerPracticePath } from '../lib/practiceRoute'
import { ownerAdsEligible } from '../lib/adEligibility'
import { orderedAlbumSongs, reorderedAlbumSongIds, type AlbumOrderDirection } from '../lib/albumOrder'
import type { AccountPlan, AppStatus, SharedAlbum, Song, SourceType } from '../types'
import { NonPersonalizedAdSlot } from './NonPersonalizedAdSlot'
import { userMessageForError } from '../lib/userError'
import { useI18n } from '../i18n/useI18n'
import { ShareExpiryDialog } from './ShareExpiryDialog'
import { requestedShareExpiresAt, saveShareExpiryChoice, type ShareExpiryChoice } from '../lib/shareExpiry'
import {
  LIBRARY_PAGE_SIZES,
  LIBRARY_PAGE_SIZE_STORAGE_KEY,
  getLibraryPagination,
  normalizeLibraryPageSize,
  type LibraryPageSize,
} from '../lib/libraryPagination'

function songSourceLabel(sourceType: SourceType, t: (key: string) => string): string {
  return t(sourceType === 'YOUTUBE' ? 'vault.source.youtube' : 'vault.source.audioFile')
}

function songShareUrl(song: Song): string {
  return new URL(`/s/${encodeURIComponent(song.shareId)}`, location.origin).toString()
}

function albumShareUrl(album: SharedAlbum): string {
  return new URL(`/a/${encodeURIComponent(album.shareToken)}`, location.origin).toString()
}

function formatDate(value: string, locale: string): string {
  return new Date(value).toLocaleDateString(locale)
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

type SortMode = 'album' | 'recent' | 'title' | 'expires'
type AlbumActionMode = 'add' | 'move'
type MenuAnchor = {
  left: number
  right: number
  top: number
  bottom: number
}
type Scope = { type: 'all' } | { type: 'unfiled' } | { type: 'album'; albumId: number }

type ShareExpiryTarget =
  | { kind: 'song'; id: number; action: 'start' | 'change' }
  | { kind: 'album'; id: number; action: 'start' | 'change' }

function songShareIsActive(song: Song): boolean {
  return Boolean(
    song.shareActive
    && song.shareExpiresAt
    && new Date(song.shareExpiresAt).getTime() > Date.now()
  )
}

function albumShareIsActive(album: SharedAlbum): boolean {
  return Boolean(
    album.active
    && album.shareExpiresAt
    && new Date(album.shareExpiresAt).getTime() > Date.now()
  )
}


const ALL_SCOPE: Scope = { type: 'all' }
const UNFILED_SCOPE: Scope = { type: 'unfiled' }

export function SegnoVault({
  appStatus,
  plan,
  onEditSong,
  onCreateNew,
}: {
  appStatus: AppStatus | null
  plan: AccountPlan | null
  onEditSong: (songId: number) => void
  onCreateNew: () => void
}) {
  const { locale, t } = useI18n()
  const [songs, setSongs] = useState<Song[]>([])
  const [albums, setAlbums] = useState<SharedAlbum[]>([])
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [albumTitle, setAlbumTitle] = useState('')
  const [savingAlbum, setSavingAlbum] = useState(false)
  const [albumSearch, setAlbumSearch] = useState('')
  const [songSearch, setSongSearch] = useState('')
  const [sortMode, setSortMode] = useState<SortMode>('recent')
  const [scope, setScope] = useState<Scope>(ALL_SCOPE)
  const [selectedSongIds, setSelectedSongIds] = useState<number[]>([])
  const [menuSongId, setMenuSongId] = useState<number | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<MenuAnchor | null>(null)
  const [actionMode, setActionMode] = useState<AlbumActionMode | null>(null)
  const [actionSongIds, setActionSongIds] = useState<number[]>([])
  const [sourceAlbumId, setSourceAlbumId] = useState<number | null>(null)
  const [targetAlbumId, setTargetAlbumId] = useState<number | null>(null)
  const [shareExpiryTarget, setShareExpiryTarget] = useState<ShareExpiryTarget | null>(null)
  const [libraryPageSize, setLibraryPageSize] = useState<LibraryPageSize>(() => {
    try {
      return normalizeLibraryPageSize(window.localStorage.getItem(LIBRARY_PAGE_SIZE_STORAGE_KEY))
    } catch {
      return 30
    }
  })
  const [libraryPage, setLibraryPage] = useState(1)

  const songById = useMemo(
    () => new Map(songs.map((song) => [song.id, song])),
    [songs],
  )

  useEffect(() => {
    void refresh()
  }, [])

  useEffect(() => {
    if (!message) return
    const handle = window.setTimeout(() => setMessage(''), 3200)
    return () => window.clearTimeout(handle)
  }, [message])

  const songAlbumMap = useMemo(() => {
    const map = new Map<number, SharedAlbum[]>()
    songs.forEach((song) => map.set(song.id, []))
    albums.forEach((album) => {
      album.songs.forEach((item) => {
        const current = map.get(item.songId) ?? []
        current.push(album)
        map.set(item.songId, current)
      })
    })
    return map
  }, [albums, songs])

  const sidebarAlbums = useMemo(() => {
    const keyword = albumSearch.trim().toLocaleLowerCase()
    const ordered = [...albums].sort((left, right) => left.title.localeCompare(right.title, locale))
    if (!keyword) return ordered
    return ordered.filter((album) => album.title.toLocaleLowerCase().includes(keyword))
  }, [albumSearch, albums, locale])

  const selectedAlbum = useMemo(() => {
    if (scope.type !== 'album') return null
    return albums.find((album) => album.id === scope.albumId) ?? null
  }, [albums, scope])

  const filteredSongs = useMemo(() => {
    const membershipMap = songAlbumMap
    const keyword = songSearch.trim().toLocaleLowerCase()

    let scopedSongs = songs.filter((song) => {
      const memberships = membershipMap.get(song.id) ?? []
      if (scope.type === 'all') return true
      if (scope.type === 'unfiled') return memberships.length === 0
      return memberships.some((album) => album.id === scope.albumId)
    })

    if (keyword) {
      scopedSongs = scopedSongs.filter((song) => {
        const memberships = membershipMap.get(song.id) ?? []
        const haystack = [
          song.title,
          songSourceLabel(song.sourceType, t),
          ...memberships.map((album) => album.title),
        ]
          .join(' ')
          .toLocaleLowerCase()
        return haystack.includes(keyword)
      })
    }

    const nextSongs = [...scopedSongs]
    nextSongs.sort((left, right) => {
      switch (sortMode) {
        case 'album': {
          if (scope.type !== 'album') {
            return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
          }
          const album = albums.find((item) => item.id === scope.albumId)
          const orderMap = new Map(
            orderedAlbumSongs(album?.songs ?? []).map((item, index) => [item.songId, index]),
          )
          return (orderMap.get(left.id) ?? Number.MAX_SAFE_INTEGER)
            - (orderMap.get(right.id) ?? Number.MAX_SAFE_INTEGER)
        }
        case 'title':
          return left.title.localeCompare(right.title, locale)
        case 'expires':
          return new Date(left.expiresAt).getTime() - new Date(right.expiresAt).getTime()
        case 'recent':
        default:
          return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
      }
    })
    return nextSongs
  }, [scope, songSearch, songAlbumMap, songs, sortMode, albums, locale])

  useEffect(() => {
    const visibleIds = new Set(filteredSongs.map((song) => song.id))
    setSelectedSongIds((current) => current.filter((id) => visibleIds.has(id)))
  }, [filteredSongs])

  useEffect(() => {
    setLibraryPage(1)
  }, [scope, songSearch, sortMode])

  useEffect(() => {
    setSortMode((current) => {
      if (scope.type === 'album') return 'album'
      return current === 'album' ? 'recent' : current
    })
  }, [scope])

  const libraryPagination = useMemo(
    () => getLibraryPagination(filteredSongs.length, libraryPageSize, libraryPage),
    [filteredSongs.length, libraryPage, libraryPageSize],
  )

  useEffect(() => {
    setLibraryPage((current) => Math.min(current, libraryPagination.totalPages))
  }, [libraryPagination.totalPages])

  const visibleSongs = useMemo(
    () => filteredSongs.slice(libraryPagination.startIndex, libraryPagination.endIndex),
    [filteredSongs, libraryPagination.endIndex, libraryPagination.startIndex],
  )

  async function refresh() {
    setLoading(true)
    try {
      const [nextSongs, nextAlbums] = await Promise.all([
        api.listSongs(),
        api.listAlbums(),
      ])
      setSongs(nextSongs)
      setAlbums(nextAlbums)
      setMessage('')
    } catch (error) {
      setMessage(userMessageForError(error, t('vault.error.load')))
    } finally {
      setLoading(false)
    }
  }

  function albumIdsForSong(songId: number): number[] {
    return (songAlbumMap.get(songId) ?? []).map((album) => album.id)
  }

  function albumsForSong(songId: number): SharedAlbum[] {
    return songAlbumMap.get(songId) ?? []
  }

  function isSelected(songId: number): boolean {
    return selectedSongIds.includes(songId)
  }

  function toggleSongSelection(songId: number) {
    setSelectedSongIds((current) => (
      current.includes(songId)
        ? current.filter((id) => id !== songId)
        : [...current, songId]
    ))
  }

  function toggleSelectAllVisible(checked: boolean) {
    if (checked) {
      setSelectedSongIds(visibleSongs.map((song) => song.id))
      return
    }
    setSelectedSongIds([])
  }

  async function createAlbum(event: React.FormEvent) {
    event.preventDefault()
    const title = albumTitle.trim()
    if (!title) {
      setMessage(t('vault.album.nameRequired'))
      return
    }

    setSavingAlbum(true)
    try {
      const created = await api.createAlbum({ title, songIds: [] })
      setAlbums((current) => [created, ...current])
      setAlbumTitle('')
      setScope({ type: 'album', albumId: created.id })
      setMessage(t('vault.album.created', { title: created.title }))
    } catch (error) {
      setMessage(userMessageForError(error, t('vault.album.createError')))
    } finally {
      setSavingAlbum(false)
    }
  }

  async function renameAlbum(album: SharedAlbum) {
    const nextTitle = prompt(t('vault.album.renamePrompt'), album.title)?.trim()
    if (!nextTitle || nextTitle === album.title) return
    try {
      const updated = await api.updateAlbum(album.id, {
        title: nextTitle,
        songIds: [...album.songs]
          .sort((left, right) => left.order - right.order)
          .map((item) => item.songId),
      })
      setAlbums((current) => current.map((item) => item.id === updated.id ? updated : item))
      setMessage(t('vault.album.renamed', { title: updated.title }))
    } catch (error) {
      setMessage(userMessageForError(error, t('vault.album.renameError')))
    }
  }

  async function moveAlbumSongOrder(
    album: SharedAlbum,
    songId: number,
    direction: AlbumOrderDirection,
  ) {
    const songIds = reorderedAlbumSongIds(album.songs, songId, direction)
    if (!songIds) return

    try {
      const updated = await api.updateAlbum(album.id, {
        title: album.title,
        songIds,
      })
      setAlbums((current) => current.map((item) => item.id === updated.id ? updated : item))
      setSortMode('album')
      setMessage(t('vault.album.orderChanged'))
    } catch (error) {
      setMessage(userMessageForError(error, t('vault.album.orderError')))
    }
  }

  async function shareAlbumLink(album: SharedAlbum) {
    const url = albumShareUrl(album)
    if (navigator.share) {
      await navigator.share({
        title: `${album.title} · mioSegno`,
        text: t('vault.share.albumText', { title: album.title }),
        url,
      })
      setMessage(t('vault.share.menuOpened', { expiry: album.shareExpiresAt ? formatDate(album.shareExpiresAt, locale) : t('vault.share.policyExpiry') }))
      return
    }
    const copied = await copyText(url)
    setMessage(copied
      ? t('vault.share.albumCopied', { expiry: album.shareExpiresAt ? formatDate(album.shareExpiresAt, locale) : t('vault.share.policyExpiry') })
      : t('vault.share.albumAddress', { url }))
  }

  async function shareAlbum(album: SharedAlbum) {
    if (!plan?.albumSharingEnabled) {
      setMessage(t('vault.share.albumUnavailable'))
      return
    }

    if (albumShareIsActive(album)) {
      try {
        await shareAlbumLink(album)
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
        setMessage(userMessageForError(error, t('vault.share.albumError')))
      }
      return
    }

    setShareExpiryTarget({ kind: 'album', id: album.id, action: 'start' })
  }

  function changeAlbumShareExpiry(album: SharedAlbum) {
    if (!plan?.albumSharingEnabled || !albumShareIsActive(album)) return
    setShareExpiryTarget({ kind: 'album', id: album.id, action: 'change' })
  }

  async function stopAlbumShare(album: SharedAlbum) {
    if (!album.active) return
    if (!confirm(t('vault.share.albumStopConfirm', { title: album.title }))) return
    try {
      await api.deactivateAlbum(album.id)
      setAlbums((current) => current.map((item) => item.id === album.id ? { ...item, active: false } : item))
      setMessage(t('vault.share.albumStopped'))
    } catch (error) {
      setMessage(userMessageForError(error, t('vault.share.albumStopError')))
    }
  }

  async function deleteAlbum(album: SharedAlbum) {
    const songCount = album.songs.length
    const shareNote = album.active ? t('vault.album.deleteShareNote') : ''
    if (!confirm(t('vault.album.deleteConfirm', { title: album.title, count: songCount, shareNote }))) return

    try {
      await api.deleteAlbum(album.id)
      setScope(UNFILED_SCOPE)
      setSelectedSongIds([])
      await refresh()
      setMessage(t('vault.album.deleted', { title: album.title }))
    } catch (error) {
      setMessage(userMessageForError(error, t('vault.album.deleteError')))
    }
  }

  async function removeFromAlbum(album: SharedAlbum, songIds: number[]) {
    if (songIds.length === 0) return
    const names = songIds
      .map((songId) => songById.get(songId)?.title)
      .filter((title): title is string => Boolean(title))
    const promptText = songIds.length === 1
      ? t('vault.album.removeSingleConfirm', { song: names[0] ?? t('vault.selection.song'), album: album.title })
      : t('vault.album.removeManyConfirm', { count: songIds.length, album: album.title })
    if (!confirm(promptText)) return

    try {
      for (const songId of songIds) {
        await api.removeSongFromAlbum(album.id, songId)
      }
      await refresh()
      setSelectedSongIds([])
      setMessage(songIds.length === 1
        ? t('vault.album.removedSingle', { song: names[0] ?? t('vault.selection.song'), album: album.title })
        : t('vault.album.removedMany', { count: songIds.length, album: album.title }))
    } catch (error) {
      setMessage(userMessageForError(error, t('vault.album.removeError')))
    }
  }

  async function shareSongLink(song: Song) {
    const url = songShareUrl(song)
    if (navigator.share) {
      await navigator.share({
        title: `${song.title} · mioSegno`,
        text: t('vault.share.songText', { title: song.title }),
        url,
      })
      setMessage(t('vault.share.menuOpened', { expiry: song.shareExpiresAt ? formatDate(song.shareExpiresAt, locale) : t('vault.share.policyExpiry') }))
      return
    }
    const copied = await copyText(url)
    setMessage(copied
      ? t('vault.share.songCopied', { expiry: song.shareExpiresAt ? formatDate(song.shareExpiresAt, locale) : t('vault.share.policyExpiry') })
      : t('vault.share.songAddress', { url }))
  }

  async function shareSong(song: Song) {
    if (!plan?.sharingEnabled) {
      setMessage(t('vault.share.planUnavailable'))
      return
    }
    if (song.sourceType === 'FILE' && !appStatus?.anonymousSharedPlaybackEnabled) {
      setMessage(t('vault.share.fileUnavailable'))
      return
    }

    if (songShareIsActive(song)) {
      try {
        await shareSongLink(song)
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
        setMessage(userMessageForError(error, t('vault.share.songError')))
      }
      return
    }

    setShareExpiryTarget({ kind: 'song', id: song.id, action: 'start' })
  }

  function changeSongShareExpiry(song: Song) {
    if (!plan?.sharingEnabled || !songShareIsActive(song)) return
    setShareExpiryTarget({ kind: 'song', id: song.id, action: 'change' })
  }

  async function confirmShareExpiry(choice: ShareExpiryChoice) {
    if (!shareExpiryTarget) return

    const requested = requestedShareExpiresAt(choice)

    if (shareExpiryTarget.kind === 'song') {
      const song = songById.get(shareExpiryTarget.id)
      if (!song) throw new Error(t('vault.share.songMissing'))

      let rightsConfirmed = Boolean(song.shareRightsConfirmed)
      if (shareExpiryTarget.action === 'start' && !rightsConfirmed) {
        rightsConfirmed = window.confirm(
          t('vault.share.rightsConfirm'),
        )
        if (!rightsConfirmed) {
          setShareExpiryTarget(null)
          return
        }
      }

      const updated = await api.activateSongShare(song.id, rightsConfirmed, requested)
      setSongs((current) => current.map((item) => item.id === updated.id ? updated : item))
      saveShareExpiryChoice(choice)
      setShareExpiryTarget(null)

      if (shareExpiryTarget.action === 'start') {
        await shareSongLink(updated)
      } else {
        setMessage(t('vault.share.songExpiryChanged', { expiry: updated.shareExpiresAt ? formatDate(updated.shareExpiresAt, locale) : t('vault.share.policyExpiry') }))
      }
      return
    }

    const album = albums.find((item) => item.id === shareExpiryTarget.id)
    if (!album) throw new Error(t('vault.share.albumMissing'))

    const updated = await api.activateAlbumShare(album.id, requested)
    setAlbums((current) => current.map((item) => item.id === updated.id ? updated : item))
    saveShareExpiryChoice(choice)
    setShareExpiryTarget(null)

    if (shareExpiryTarget.action === 'start') {
      await shareAlbumLink(updated)
    } else {
      setMessage(t('vault.share.albumExpiryChanged', { expiry: updated.shareExpiresAt ? formatDate(updated.shareExpiresAt, locale) : t('vault.share.policyExpiry') }))
    }
  }

  async function stopSongShare(song: Song) {
    if (!song.shareActive) return
    if (!confirm(t('vault.share.songStopConfirm', { title: song.title }))) return
    try {
      const updated = await api.deactivateSongShare(song.id)
      setSongs((current) => current.map((item) => item.id === updated.id ? updated : item))
      setMessage(t('vault.share.songStopped'))
    } catch (error) {
      setMessage(userMessageForError(error, t('vault.share.songStopError')))
    }
  }

  function exportSegnoFile(song: Song) {
    try {
      downloadSegnoFile(song)
      setMessage(t('vault.export.done', { title: song.title }))
    } catch (error) {
      setMessage(userMessageForError(error, t('vault.export.error')))
    }
  }

  async function deleteSong(song: Song) {
    const mediaNote = song.sourceType === 'FILE' ? t('vault.delete.mediaNote') : ''
    if (!confirm(t('vault.delete.confirm', { title: song.title, mediaNote }))) return
    try {
      await api.deleteSong(song.id)
      if (song.mediaFileId) {
        await audioStore.remove(audioCacheKey(song.mediaFileId, song.shareId)).catch(() => undefined)
      }
      await refresh()
      setSelectedSongIds([])
      closeSongMenu()
      setMessage(t('vault.delete.done'))
    } catch (error) {
      setMessage(userMessageForError(error, t('vault.delete.error')))
    }
  }

  function openSingleAlbumAction(song: Song, mode: AlbumActionMode) {
    const membershipIds = albumIdsForSong(song.id)
    setActionSongIds([song.id])
    setActionMode(mode)
    if (mode === 'add') {
      const firstTarget = albums.find((album) => !membershipIds.includes(album.id))?.id ?? null
      setSourceAlbumId(null)
      setTargetAlbumId(firstTarget)
    } else {
      const firstSource = membershipIds[0] ?? null
      const firstTarget = albums.find((album) => album.id !== firstSource)?.id ?? null
      setSourceAlbumId(firstSource)
      setTargetAlbumId(firstTarget)
    }
    setMenuSongId(null)
    setMenuAnchor(null)
  }

  function openBulkAddAction() {
    setActionSongIds(selectedSongIds)
    setActionMode('add')
    setSourceAlbumId(null)
    setTargetAlbumId(albums[0]?.id ?? null)
  }

  function closeAlbumAction() {
    setActionMode(null)
    setActionSongIds([])
    setSourceAlbumId(null)
    setTargetAlbumId(null)
  }

  function openSongMenu(songId: number, event: ReactMouseEvent<HTMLButtonElement>) {
    const rect = event.currentTarget.getBoundingClientRect()
    setMenuAnchor({ left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom })
    setMenuSongId(songId)
  }

  function closeSongMenu() {
    setMenuSongId(null)
    setMenuAnchor(null)
  }

  async function applyAlbumAction() {
    if (actionSongIds.length === 0 || targetAlbumId == null || actionMode == null) return
    const titlePreview = actionSongIds.length === 1 ? songById.get(actionSongIds[0])?.title ?? t('vault.selection.song') : t('vault.selection.songs', { count: actionSongIds.length })

    try {
      if (actionMode === 'add') {
        const alreadyMemberSet = new Set(
          actionSongIds.filter((songId) => albumIdsForSong(songId).includes(targetAlbumId)),
        )
        const targetSongIds = actionSongIds.filter((songId) => !alreadyMemberSet.has(songId))
        if (targetSongIds.length === 0) {
          setMessage(t('vault.albumAction.alreadyMember'))
          return
        }
        for (const songId of targetSongIds) {
          await api.addSongToAlbum(targetAlbumId, songId)
        }
        const target = albums.find((album) => album.id === targetAlbumId)
        setMessage(t('vault.albumAction.added', { songs: titlePreview, album: target?.title ?? t('vault.selection.album') }))
      } else {
        if (sourceAlbumId == null) {
          setMessage(t('vault.albumAction.sourceRequired'))
          return
        }
        if (actionSongIds.length !== 1) {
          setMessage(t('vault.albumAction.moveOneOnly'))
          return
        }
        if (sourceAlbumId === targetAlbumId) {
          setMessage(t('vault.albumAction.differentAlbums'))
          return
        }
        await api.moveSongBetweenAlbums(sourceAlbumId, actionSongIds[0], targetAlbumId)
        const from = albums.find((album) => album.id === sourceAlbumId)
        const to = albums.find((album) => album.id === targetAlbumId)
        setMessage(t('vault.albumAction.moved', { songs: titlePreview, from: from?.title ?? t('vault.selection.existingAlbum'), to: to?.title ?? t('vault.selection.newAlbum') }))
      }
      closeAlbumAction()
      await refresh()
      setSelectedSongIds([])
    } catch (error) {
      setMessage(userMessageForError(error, t('vault.albumAction.error')))
    }
  }

  const actionSongs = actionSongIds
    .map((songId) => songById.get(songId))
    .filter((song): song is Song => Boolean(song))
  const actionSong = actionSongs[0] ?? null
  const actionMembershipIds = actionSong ? albumIdsForSong(actionSong.id) : []
  const addTargets = albums.filter((album) => (
    actionMode !== 'add'
      ? true
      : actionSongIds.some((songId) => !albumIdsForSong(songId).includes(album.id))
  ))
  const sourceAlbums = albums.filter((album) => actionMembershipIds.includes(album.id))
  const moveTargets = albums.filter((album) => album.id !== sourceAlbumId)
  const selectedCount = selectedSongIds.length
  const allVisibleSelected = visibleSongs.length > 0 && visibleSongs.every((song) => selectedSongIds.includes(song.id))
  const canCreateAlbum = Boolean(albumTitle.trim()) && !savingAlbum
  const canPracticeSong = (song: Song) => song.sourceType !== 'FILE' || Boolean(appStatus?.mediaApiEnabled)
  const canShareSong = (song: Song) => Boolean(plan?.sharingEnabled) && (song.sourceType !== 'FILE' || Boolean(appStatus?.anonymousSharedPlaybackEnabled))
  const selectedScopeLabel = scope.type === 'all'
    ? t('vault.scope.all')
    : scope.type === 'unfiled'
      ? t('vault.scope.unfiled')
      : selectedAlbum?.title ?? t('vault.scope.album')
  const selectedScopeDescription = scope.type === 'all'
    ? t('vault.scope.allDescription', { count: songs.length })
    : scope.type === 'unfiled'
      ? t('vault.scope.unfiledDescription')
      : selectedAlbum?.active
        ? t('vault.scope.activeAlbumDescription', { count: selectedAlbum.songs.length })
        : t('vault.scope.albumDescription', { count: selectedAlbum?.songs.length ?? 0 })

  const menuPopoverStyle: CSSProperties | undefined = menuAnchor
    ? (() => {
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const gap = 8
        const side = 12
        const desiredWidth = Math.min(312, Math.max(248, viewportWidth - side * 2))
        const left = Math.max(side, Math.min(menuAnchor.right - desiredWidth, viewportWidth - desiredWidth - side))
        const openAbove = menuAnchor.top > viewportHeight * 0.56
        return openAbove
          ? { left, bottom: Math.max(side, viewportHeight - menuAnchor.top + gap), width: desiredWidth }
          : { left, top: Math.max(side, menuAnchor.bottom + gap), width: desiredWidth }
      })()
    : undefined


  return (
    <section className="vault-page">
      <article className="card vault-main vault-main-explorer">
        <div className="section-title-row vault-title-row">
          <div>
            <p className="eyebrow">{t('vault.header.eyebrow')}</p>
            <h2>{t('vault.header.title')}</h2>
          </div>
          <div className="control-row vault-top-actions">
            <button type="button" className="button primary" onClick={onCreateNew}>{t('vault.action.createSong')}</button>
            <button type="button" className="button ghost" onClick={() => void refresh()}>{t('common.refresh')}</button>
          </div>
        </div>

        <div className="vault-explorer-layout">
          <aside className="vault-sidebar">
            <div className="vault-sidebar-header">
              <h3>{t('vault.sidebar.albums')}</h3>
              <small className="muted">{t('vault.sidebar.structure')}</small>
            </div>

            <input
              className="vault-sidebar-search"
              value={albumSearch}
              onChange={(event) => setAlbumSearch(event.target.value)}
              placeholder={t('vault.sidebar.searchPlaceholder')}
              aria-label={t('vault.sidebar.searchAria')}
            />

            <form className="vault-album-create vault-album-create-compact" onSubmit={(event) => void createAlbum(event)}>
              <input
                value={albumTitle}
                maxLength={120}
                onChange={(event) => setAlbumTitle(event.target.value)}
                placeholder={t('vault.sidebar.newAlbumPlaceholder')}
                aria-label={t('vault.sidebar.newAlbumAria')}
              />
              <button type="submit" className="button secondary" disabled={!canCreateAlbum}>
                {savingAlbum ? t('vault.sidebar.creatingAlbum') : t('vault.sidebar.createAlbum')}
              </button>
            </form>

            <div className="vault-sidebar-list" role="list" aria-label={t('vault.sidebar.albumListAria')}>
              <button type="button" className={scope.type === 'all' ? 'vault-sidebar-item active' : 'vault-sidebar-item'} onClick={() => setScope(ALL_SCOPE)}>
                <span>{t('vault.scope.all')}</span>
                <strong>{songs.length}</strong>
              </button>
              <button type="button" className={scope.type === 'unfiled' ? 'vault-sidebar-item active' : 'vault-sidebar-item'} onClick={() => setScope(UNFILED_SCOPE)}>
                <span>{t('vault.scope.unfiled')}</span>
                <strong>{songs.filter((song) => albumsForSong(song.id).length === 0).length}</strong>
              </button>
              {sidebarAlbums.length === 0 ? (
                <p className="muted vault-sidebar-empty">{t('vault.sidebar.noAlbums')}</p>
              ) : (
                sidebarAlbums.map((album) => (
                  <button
                    type="button"
                    key={album.id}
                    className={scope.type === 'album' && scope.albumId === album.id ? 'vault-sidebar-item active' : 'vault-sidebar-item'}
                    onClick={() => setScope({ type: 'album', albumId: album.id })}
                  >
                    <span className="vault-sidebar-item-title">{album.title}</span>
                    <strong>{album.songs.length}</strong>
                  </button>
                ))
              )}
            </div>
          </aside>

          <section className="vault-content">
            <header className="vault-content-header">
              <div>
                <p className="eyebrow">{t('vault.content.selectedView')}</p>
                <h3>{selectedScopeLabel}</h3>
                <p className="muted">{selectedScopeDescription}</p>
              </div>
              {selectedAlbum && (
                <div className="control-row vault-selected-album-actions">
                  <button type="button" className="button ghost" onClick={() => void renameAlbum(selectedAlbum)}>{t('vault.action.rename')}</button>
                  {plan?.albumSharingEnabled && <button type="button" className="button secondary" onClick={() => void shareAlbum(selectedAlbum)}>{albumShareIsActive(selectedAlbum) ? t('vault.action.albumLink') : t('vault.action.startShare')}</button>}
                  {plan?.albumSharingEnabled && albumShareIsActive(selectedAlbum) && <button type="button" className="button secondary" onClick={() => changeAlbumShareExpiry(selectedAlbum)}>{t('vault.action.changeSharePeriod')}</button>}
                  {plan?.albumSharingEnabled && albumShareIsActive(selectedAlbum) && <button type="button" className="button ghost" onClick={() => void stopAlbumShare(selectedAlbum)}>{t('vault.action.stopShare')}</button>}
                  <button type="button" className="button danger ghost" onClick={() => void deleteAlbum(selectedAlbum)}>{t('vault.action.deleteAlbum')}</button>
                </div>
              )}
            </header>

            <div className="vault-toolbar">
              <input
                value={songSearch}
                onChange={(event) => setSongSearch(event.target.value)}
                placeholder={t('vault.search.placeholder')}
                aria-label={t('vault.search.aria')}
              />
              <select value={sortMode} onChange={(event) => setSortMode(event.target.value as SortMode)} aria-label={t('vault.sort.aria')}>
                {selectedAlbum && <option value="album">{t('vault.sort.album')}</option>}
                <option value="recent">{t('vault.sort.recent')}</option>
                <option value="title">{t('vault.sort.title')}</option>
                <option value="expires">{t('vault.sort.expires')}</option>
              </select>
            </div>

            {selectedCount > 0 && (
              <div className="vault-bulk-bar" role="status">
                <strong>{t('vault.selection.selectedCount', { count: selectedCount })}</strong>
                <div className="control-row vault-bulk-actions">
                  <button type="button" className="button ghost" disabled={albums.length === 0} onClick={openBulkAddAction}>{t('vault.action.addSelectedToAlbum')}</button>
                  {scope.type === 'album' && selectedAlbum && (
                    <button type="button" className="button ghost" onClick={() => void removeFromAlbum(selectedAlbum, selectedSongIds)}>{t('vault.action.removeFromCurrentAlbum')}</button>
                  )}
                  <button type="button" className="button ghost" onClick={() => setSelectedSongIds([])}>{t('vault.action.clearSelection')}</button>
                </div>
              </div>
            )}

            <NonPersonalizedAdSlot
              status={appStatus}
              eligible={ownerAdsEligible(plan)}
              placement="owner-vault"
            />

            {loading ? (
              <p className="muted">{t('vault.loading')}</p>
            ) : filteredSongs.length === 0 ? (
              <div className="vault-empty-state">
                <strong>{t('vault.empty.title')}</strong>
                <p className="muted">{t('vault.empty.description')}</p>
              </div>
            ) : (
              <div className="vault-song-table">
                <div className="vault-song-table-head">
                  <label className="vault-checkbox-cell">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={(event) => toggleSelectAllVisible(event.target.checked)}
                      aria-label={t('vault.selection.selectVisibleAria')}
                    />
                  </label>
                  <span>{t('vault.scope.all')}</span>
                  <span>{t('vault.table.quickActions')}</span>
                </div>
                <div className="vault-song-list compact">
                  {visibleSongs.map((song) => {
                    const memberships = albumsForSong(song.id)
                    const practiceEnabled = canPracticeSong(song)
                    const albumSongs = selectedAlbum ? orderedAlbumSongs(selectedAlbum.songs) : []
                    const albumIndex = albumSongs.findIndex((item) => item.songId === song.id)
                    return (
                      <article className="vault-song-row vault-song-row-compact" key={song.id}>
                        <label className="vault-checkbox-cell">
                          <input
                            type="checkbox"
                            checked={isSelected(song.id)}
                            onChange={() => toggleSongSelection(song.id)}
                            aria-label={t('vault.song.selectAria', { title: song.title })}
                          />
                        </label>
                        <div className="vault-song-info">
                          <strong>{song.title}</strong>
                          <small>{song.shareActive && song.shareExpiresAt
                            ? t('vault.song.metaShared', { source: songSourceLabel(song.sourceType, t), count: song.segnos.length, expires: formatDate(song.expiresAt, locale), shareExpires: formatDate(song.shareExpiresAt, locale) })
                            : t('vault.song.metaNotShared', { source: songSourceLabel(song.sourceType, t), count: song.segnos.length, expires: formatDate(song.expiresAt, locale) })}</small>
                          {song.sourceType === 'FILE' && song.shareDownloadLimit != null && (song.shareActive || (song.shareDownloadCount ?? 0) > 0) && (
                            <small className="vault-song-download-meta">
                              {t('vault.song.downloadUsage', {
                                used: song.shareDownloadCount ?? 0,
                                limit: song.shareDownloadLimit,
                                remaining: song.shareDownloadsRemaining ?? Math.max(0, song.shareDownloadLimit - (song.shareDownloadCount ?? 0)),
                              })}
                            </small>
                          )}
                          <small className="vault-song-album-meta" title={memberships.length === 0 ? t('vault.scope.unfiled') : memberships.map((album) => album.title).join(', ')}>
                            {memberships.length === 0 ? t('vault.scope.unfiled') : t('vault.song.albumMembership', { albums: memberships.map((album) => album.title).join(', ') })}
                          </small>
                        </div>
                        <div className="control-row vault-song-actions-inline">
                          {selectedAlbum && (
                            <span className="vault-album-order-controls" aria-label={t('vault.song.albumOrderAria', { title: song.title })}>
                              <button
                                type="button"
                                className="button ghost compact"
                                disabled={albumIndex <= 0}
                                title={t('vault.song.moveUpTitle')}
                                aria-label={t('vault.song.moveUpAria', { title: song.title })}
                                onClick={() => void moveAlbumSongOrder(selectedAlbum, song.id, -1)}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="button ghost compact"
                                disabled={albumIndex < 0 || albumIndex >= albumSongs.length - 1}
                                title={t('vault.song.moveDownTitle')}
                                aria-label={t('vault.song.moveDownAria', { title: song.title })}
                                onClick={() => void moveAlbumSongOrder(selectedAlbum, song.id, 1)}
                              >
                                ↓
                              </button>
                            </span>
                          )}
                          {practiceEnabled ? (
                            <a className="button secondary compact" href={ownerPracticePath(song.id)} target="_blank" rel="noreferrer">{t('vault.action.openPractice')}</a>
                          ) : (
                            <button type="button" className="button secondary compact" disabled title={t('vault.song.practiceUnavailable')}>{t('vault.action.openPractice')}</button>
                          )}
                          <button type="button" className="button ghost compact vault-menu-button" onClick={(event) => openSongMenu(song.id, event)} aria-label={t('vault.song.menuAria', { title: song.title })}>
                            ⋯
                          </button>
                        </div>
                      </article>
                    )
                  })}
                </div>
                <div className="library-pagination" aria-label={t('vault.pagination.aria')}>
                  <div className="library-pagination-summary">
                    <span>{t('vault.pagination.perPage')}</span>
                    <div className="library-page-size-options" role="group" aria-label={t('vault.pagination.pageSizeAria')}>
                      {LIBRARY_PAGE_SIZES.map((size) => (
                        <button
                          key={size}
                          type="button"
                          className={libraryPageSize === size ? 'is-active' : undefined}
                          aria-pressed={libraryPageSize === size}
                          onClick={() => {
                            setLibraryPageSize(size)
                            setLibraryPage(1)
                            try {
                              window.localStorage.setItem(LIBRARY_PAGE_SIZE_STORAGE_KEY, String(size))
                            } catch {
                              // 브라우저 저장소가 제한되어도 페이지 기능은 계속 동작합니다.
                            }
                          }}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                    <span>
                      {t('vault.pagination.summary', { start: libraryPagination.startNumber, end: libraryPagination.endNumber, total: filteredSongs.length })}
                    </span>
                  </div>

                  <nav className="library-page-navigation" aria-label={t('vault.pagination.pageAria')}>
                    <button
                      type="button"
                      disabled={libraryPagination.currentPage === 1}
                      onClick={() => setLibraryPage(1)}
                    >
                      {t('vault.pagination.first')}
                    </button>
                    <button
                      type="button"
                      disabled={libraryPagination.currentPage === 1}
                      onClick={() => setLibraryPage(Math.max(1, libraryPagination.currentPage - 1))}
                    >
                      {t('vault.pagination.previous')}
                    </button>

                    {libraryPagination.items.map((item) =>
                      typeof item === 'number' ? (
                        <button
                          key={item}
                          type="button"
                          className={libraryPagination.currentPage === item ? 'is-active' : undefined}
                          aria-current={libraryPagination.currentPage === item ? 'page' : undefined}
                          onClick={() => setLibraryPage(item)}
                        >
                          {item}
                        </button>
                      ) : (
                        <span key={item} className="library-page-ellipsis" aria-hidden="true">
                          …
                        </span>
                      ),
                    )}

                    <button
                      type="button"
                      disabled={libraryPagination.currentPage === libraryPagination.totalPages}
                      onClick={() => setLibraryPage(Math.min(libraryPagination.totalPages, libraryPagination.currentPage + 1))}
                    >
                      {t('vault.pagination.next')}
                    </button>
                    <button
                      type="button"
                      disabled={libraryPagination.currentPage === libraryPagination.totalPages}
                      onClick={() => setLibraryPage(libraryPagination.totalPages)}
                    >
                      {t('vault.pagination.last')}
                    </button>
                  </nav>
                </div>
              </div>
            )}
          </section>
        </div>

        {message && <p className="notice vault-message vault-toast" role="status">{message}</p>}
      </article>

      {menuSongId != null && songById.get(menuSongId) && menuAnchor && createPortal(
        <div className="vault-context-layer" role="presentation">
          <button type="button" className="vault-context-backdrop" aria-label={t('vault.menu.closeAria')} onClick={closeSongMenu} />
          <section className="card vault-song-menu-dialog vault-song-menu-popover" style={menuPopoverStyle} role="dialog" aria-modal="false" aria-labelledby="vault-song-menu-title">
            <div className="section-title-row">
              <div>
                <p className="eyebrow">{t('vault.menu.title')}</p>
                <h3 id="vault-song-menu-title">{songById.get(menuSongId)?.title}</h3>
              </div>
              <button type="button" className="button ghost" onClick={closeSongMenu}>{t('common.close')}</button>
            </div>
            <div className="vault-menu-actions">
              <button type="button" className="button secondary" onClick={() => { const song = songById.get(menuSongId); if (song) onEditSong(song.id); closeSongMenu() }}>{t('common.edit')}</button>
              {plan?.sharingEnabled && (songShareIsActive(songById.get(menuSongId)!) ? (
                <>
                  <button type="button" className="button secondary" disabled={!canShareSong(songById.get(menuSongId)!)} onClick={() => { const song = songById.get(menuSongId); if (song) void shareSong(song); closeSongMenu() }}>{t('vault.action.songLink')}</button>
                  <button type="button" className="button secondary" onClick={() => { const song = songById.get(menuSongId); if (song) changeSongShareExpiry(song); closeSongMenu() }}>{t('vault.action.changeSharePeriod')}</button>
                  <button type="button" className="button ghost" onClick={() => { const song = songById.get(menuSongId); if (song) void stopSongShare(song); closeSongMenu() }}>{t('vault.action.stopShare')}</button>
                </>
              ) : (
                <button type="button" className="button secondary" disabled={!canShareSong(songById.get(menuSongId)!)} onClick={() => { const song = songById.get(menuSongId); if (song) void shareSong(song); closeSongMenu() }}>{t('vault.action.startShare')}</button>
              ))}
              <button type="button" className="button secondary" onClick={() => { const song = songById.get(menuSongId); if (song) exportSegnoFile(song); closeSongMenu() }}>{t('vault.action.export')}</button>
              <button type="button" className="button secondary" disabled={albums.length === 0 || albumIdsForSong(menuSongId).length === albums.length} onClick={() => { const song = songById.get(menuSongId); if (song) openSingleAlbumAction(song, 'add') }}>{t('vault.action.addToAlbum')}</button>
              <button type="button" className="button secondary" disabled={albumsForSong(menuSongId).length === 0 || albums.length < 2} onClick={() => { const song = songById.get(menuSongId); if (song) openSingleAlbumAction(song, 'move') }}>{t('vault.action.moveAlbum')}</button>
              {scope.type === 'album' && selectedAlbum && albumIdsForSong(menuSongId).includes(selectedAlbum.id) && (
                <button type="button" className="button secondary" onClick={() => { void removeFromAlbum(selectedAlbum, [menuSongId]); closeSongMenu() }}>{t('vault.action.removeFromCurrentAlbum')}</button>
              )}
              <button type="button" className="button danger ghost" onClick={() => { const song = songById.get(menuSongId); if (song) void deleteSong(song) }}>{t('common.delete')}</button>
            </div>
          </section>
        </div>
        , document.body
      )}

      {shareExpiryTarget && (() => {
        const target = shareExpiryTarget.kind === 'song'
          ? songById.get(shareExpiryTarget.id)
          : albums.find((item) => item.id === shareExpiryTarget.id)
        if (!target) return null
        return (
          <ShareExpiryDialog
            key={`${shareExpiryTarget.kind}-${shareExpiryTarget.id}-${shareExpiryTarget.action}`}
            targetType={shareExpiryTarget.kind}
            targetTitle={target.title}
            action={shareExpiryTarget.action}
            currentExpiry={target.shareExpiresAt}
            shareDownloadLimit={shareExpiryTarget.kind === 'song' && 'sourceType' in target && target.sourceType === 'FILE' ? target.shareDownloadLimit : null}
            onCancel={() => setShareExpiryTarget(null)}
            onConfirm={confirmShareExpiry}
          />
        )
      })()}

      {actionMode && createPortal(
        <div className="album-dialog-layer" role="presentation">
          <button type="button" className="album-dialog-backdrop" aria-label={t('vault.albumDialog.closeAria')} onClick={closeAlbumAction} />
          <section className="card vault-album-action-dialog" role="dialog" aria-modal="true" aria-labelledby="vault-album-action-title">
            <div className="section-title-row">
              <div>
                <p className="eyebrow">{actionMode === 'add' ? t('vault.action.addToAlbum') : t('vault.action.moveAlbum')}</p>
                <h3 id="vault-album-action-title">
                  {actionSongIds.length === 1 ? actionSong?.title : t('vault.selection.songs', { count: actionSongIds.length })}
                </h3>
              </div>
              <button type="button" className="button ghost" onClick={closeAlbumAction}>{t('common.close')}</button>
            </div>

            {actionMode === 'add' ? (
              addTargets.length === 0 ? (
                <p className="muted">{t('vault.albumDialog.noAddTarget')}</p>
              ) : (
                <label>
                  {t('vault.albumDialog.addTarget')}
                  <select value={targetAlbumId ?? ''} onChange={(event) => setTargetAlbumId(Number(event.target.value))}>
                    {addTargets.map((album) => <option value={album.id} key={album.id}>{album.title}</option>)}
                  </select>
                </label>
              )
            ) : (
              <div className="vault-move-fields">
                <label>
                  {t('vault.albumDialog.currentAlbum')}
                  <select
                    value={sourceAlbumId ?? ''}
                    onChange={(event) => {
                      const nextSource = Number(event.target.value)
                      setSourceAlbumId(nextSource)
                      if (targetAlbumId === nextSource) {
                        setTargetAlbumId(albums.find((album) => album.id !== nextSource)?.id ?? null)
                      }
                    }}
                  >
                    {sourceAlbums.map((album) => <option value={album.id} key={album.id}>{album.title}</option>)}
                  </select>
                </label>
                <label>
                  {t('vault.albumDialog.moveTarget')}
                  <select value={targetAlbumId ?? ''} onChange={(event) => setTargetAlbumId(Number(event.target.value))}>
                    {moveTargets.map((album) => <option value={album.id} key={album.id}>{album.title}</option>)}
                  </select>
                </label>
              </div>
            )}

            <div className="control-row vault-dialog-actions">
              <button type="button" className="button ghost" onClick={closeAlbumAction}>{t('common.cancel')}</button>
              <button
                type="button"
                className="button primary"
                disabled={targetAlbumId == null || (actionMode === 'move' && sourceAlbumId == null)}
                onClick={() => void applyAlbumAction()}
              >
                {actionMode === 'add' ? t('vault.action.addToAlbum') : t('vault.action.moveAlbum')}
              </button>
            </div>
          </section>
        </div>
        , document.body
      )}
    </section>
  )
}
